import React, { useEffect, useState } from 'react';
import { Modal, InputNumber, Button, Flex, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { orderFetch as v2OrderFetch, orderItemCountUpdates, orderPreOut } from '@/apis/inventoryv2';
import { OrderItemV2, OrderV2, OrderItemLocation } from '@/types/inventoryV2';
import useOrderPreOutLocationTypes from '@/hooks/useOrderPreOutLocationTypes';
import errorCodes from '@/apis/error';

interface OrderItemWithConfirmNum extends Omit<OrderItemV2, 'locations' | 'source'> {
  confirmNum: number;
  locations: Array<OrderItemLocation>;
  source: any;
}

interface ConfirmQuantityModalProps {
  orderId: string;
  onClose: () => void;
  onSucc: (order: OrderV2) => void;
}

const ConfirmQuantityModal: React.FC<ConfirmQuantityModalProps> = ({
  orderId,
  onClose,
  onSucc
}) => {
  const { t } = useTranslation();
  const selectPreOutLocationTypes = useOrderPreOutLocationTypes();
  const [order, setOrder] = useState<OrderV2 | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail();
    }
  }, [orderId]);

  const fetchOrderDetail = () => {
    setLoading(true);
    v2OrderFetch(orderId)
      .then(res => {
        // 添加 confirmNum 字段
        const itemsWithConfirmNum: OrderItemWithConfirmNum[] = res.items.map(item => ({
          ...item,
          confirmNum: item.count
        }));
        setOrder({
          ...res,
          items: itemsWithConfirmNum
        });
      })
      .catch((err: any) => {
        console.error('获取订单详情失败:', err);
        notification.error({
          placement: 'top',
          message: t('common.error'),
          description: t('warehouse.fetchError'),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 检查订单是否已确认
  const handleNumChange = (index: number, value: number | null) => {
    if (!order) return;
    const newItems = [...order.items] as OrderItemWithConfirmNum[];
    newItems[index] = {
      ...newItems[index],
      confirmNum: value || 0
    };
    setOrder({
      ...order,
      items: newItems
    });
  };

  const handleConfirm = async () => {
    if (!order) return;

    // 收集需要更新的订单项（confirmNum 不等于 count 的项）和回滚数据
    const updateItems: Array<{ orderItemId: string; count: number }> = [];
    const revertItems: Array<{ orderItemId: string; count: number }> = [];

    (order.items as OrderItemWithConfirmNum[]).forEach((item: OrderItemWithConfirmNum) => {
      if (item.confirmNum !== item.count) {
        updateItems.push({
          orderItemId: item.id,
          count: item.confirmNum
        });
        revertItems.push({
          orderItemId: item.id,
          count: item.count  // 使用原始 count
        });
      }
    });

    // 如果有需要更新的项，使用批量更新接口
    if (updateItems.length > 0) {
      setSubmitting(true);
      const locationTypes = await selectPreOutLocationTypes();
      if (!locationTypes) {
        setSubmitting(false);
        return;
      }
      // 先执行 orderItemCountUpdates（肯定成功）
      try {
        await orderItemCountUpdates(updateItems);
      } catch (error) {
        setSubmitting(false);
        notification.error({
          message: t('order.modificatFailed'),
          description: error + ''
        })
        return
      }

      try {
        // 执行 orderPreOut
        await orderPreOut(orderId, locationTypes);

        // orderPreOut 成功，更新本地数据
        notification.success({
          placement: 'top',
          message: t('order.updateOrderSuccess')
        });
        const updatedItems = (order.items as OrderItemWithConfirmNum[]).map(item => {
          const updated = updateItems.find(ui => ui.orderItemId === item.id);
          if (updated) {
            return { ...item, count: updated.count, confirmNum: updated.count };
          }
          return item;
        });
        const updatedOrder = {
          ...order,
          items: updatedItems
        };
        onSucc(updatedOrder);
        onClose();
      } catch (err: any) {
        if (err?.code === 73007 || err?.code === 73008) {
          if (Array.isArray(err?.extra)) {
            let errCodes = order.items
              .filter(item => err.extra.includes(item.productId) || err.extra.includes(item.product?.code))
              .map(item => item.product?.code ?? '')
              .join(', ')
            notification.error({
              placement: 'top',
              message: t('order.noChanges'),
              description: t('apiError.error31') + `: ${errCodes}`,
            });
          } else notification.error({
            placement: 'top',
            message: t('order.noChanges'),
            description: t('warehouse.insufficientInventory')
          });
        } else if (err?.code === 73010) {
          notification.error({
            message: t('inventory.proQtyFalse'),
            description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
          })
        } else if ([73022, 73014].includes(err)) {
          notification.error({
            message: t('inventory.calcErr')
          })
        } else if ([73024, 73028].includes(err?.code)) {
          notification.error({
            message: t('warehouse.insufficientInventory'),
            description: err?.extra?.productCode
          })
        } else {
          errorCodes[err] && notification.error({ message: errorCodes[err] })
        }
        // orderPreOut 失败，使用预先定义的 revertItems 回滚
        await orderItemCountUpdates(revertItems);

      } finally {
        setSubmitting(false);
      }
    } else {
      // 没有需要更新的项
      notification.info({
        placement: 'top',
        message: t('order.noChanges')
      });
      onClose();
    }
  };

  const btnCancel = (
    <Button onClick={onClose} disabled={submitting}>
      {t('order.cancel')}
    </Button>
  );

  const btnConfirm = (
    <Button
      loading={submitting}
      type="primary"
      onClick={handleConfirm}
    >
      {t('order.confirm')}
    </Button>
  );

  return (
    <Modal
      open={!!orderId}
      onCancel={onClose}
      title={
        <div>
          {t('order.quantityConfirm')}
        </div>
      }
      footer={[btnCancel, btnConfirm]}
      destroyOnClose
      width={1100}
      style={{ top: 20 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          Loading...
        </div>
      )}
      {!loading && order && (
        <div style={{
          maxHeight: 'calc(100vh - 300px)',
          overflowY: 'auto',
          paddingRight: '8px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px'
          }}>
            {(order.items as OrderItemWithConfirmNum[]).map((item: OrderItemWithConfirmNum, index: number) => (
              <>
                <Flex
                  key={item.id}
                  vertical
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  {/* 左边：产品信息 */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '6px',
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${item.product?.code || '-'} - ${item.product?.name || '-'}`}>
                      {item.product?.code || '-'} - {item.product?.name || '-'}
                    </div>
                    <div style={{
                      color: '#666',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${t('product.model')}: ${item.product?.model || '-'}`}>
                      {t('product.model')}: {item.product?.model || '-'}
                    </div>
                  </div>

                  {/* 右边：数量输入框 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ marginBottom: '6px', fontSize: '12px', color: '#666' }}>
                      {t('order.quantity')}
                    </div>
                    <InputNumber changeOnWheel
                      style={{ width: '100%' }}
                      min={1}
                      size="small"
                      disabled
                      value={item.count}
                      onChange={(value) => handleNumChange(index, value)}
                    />
                  </div>
                </Flex>
                <Flex
                  key={item.id}
                  vertical
                  style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  {/* 左边：产品信息 */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '6px',
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${item.product?.code || '-'} - ${item.product?.name || '-'}`}>
                      {item.product?.code || '-'} - {item.product?.name || '-'}
                    </div>
                    <div style={{
                      color: '#666',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={`${t('product.model')}: ${item.product?.model || '-'}`}>
                      {t('product.model')}: {item.product?.model || '-'}
                    </div>
                  </div>

                  {/* 右边：数量输入框 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ marginBottom: '6px', fontSize: '12px', color: '#666' }}>
                      {t('order.quantity')}
                    </div>
                    <InputNumber changeOnWheel
                      style={{ width: '100%' }}
                      min={1}
                      size="small"
                      value={item.confirmNum}
                      onChange={(value) => handleNumChange(index, value)}
                    />
                  </div>
                </Flex>
              </>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConfirmQuantityModal;
